
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  retryCount: number;
}

export class MapErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): State {
    console.error('🗺️ MapErrorBoundary: Caught error:', error);
    return { hasError: true, error, retryCount: 0 };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🗺️ MapErrorBoundary: Error details:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    console.log('🔄 MapErrorBoundary: Retrying map preview');
    this.setState(prevState => ({
      hasError: false,
      error: undefined,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="my-3 border border-red-200 rounded-lg bg-red-50 p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-800">Map Preview Error</h3>
              <p className="text-xs text-red-600 mt-1">
                {this.state.error?.message || 'Failed to load map preview'}
              </p>
              {this.state.retryCount < 3 && (
                <button
                  onClick={this.handleRetry}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-800 transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Retry ({this.state.retryCount + 1}/3)
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
